import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Layers, FolderKanban, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { cn } from '@/lib/utils';

const palette = ['#8b8a84', '#2563eb', '#0f766e', '#b45309', '#be123c', '#7c3aed'];

export function WorkspacePage() {
  const { projects, bundles, loadWorkspace } = useWorkspaceStore();
  const [projectDraft, setProjectDraft] = useState({ name: '', purpose: '', color: '#2563eb' });
  const [bundleDraft, setBundleDraft] = useState({
    name: '',
    purpose: '',
    color: '#0f766e',
    project_id: 'none',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadWorkspace().catch(error => console.error('Failed to load workspace', error));
  }, [loadWorkspace]);

  const activeProjects = projects.filter(project => project.status === 'active');
  const activeBundles = bundles.filter(bundle => bundle.status === 'active');
  const bundlesByProject = useMemo(() => {
    const map = new Map<string, number>();
    for (const bundle of activeBundles) {
      if (!bundle.projectId) continue;
      map.set(bundle.projectId, (map.get(bundle.projectId) || 0) + 1);
    }
    return map;
  }, [activeBundles]);

  async function createProject() {
    if (!projectDraft.name.trim()) return;
    setSaving(true);
    try {
      await api.workspace.createProject(projectDraft);
      setProjectDraft({ name: '', purpose: '', color: '#2563eb' });
      await loadWorkspace();
    } finally {
      setSaving(false);
    }
  }

  async function createBundle() {
    if (!bundleDraft.name.trim()) return;
    setSaving(true);
    try {
      await api.workspace.createBundle({
        ...bundleDraft,
        project_id: bundleDraft.project_id === 'none' ? null : bundleDraft.project_id,
      });
      setBundleDraft({ name: '', purpose: '', color: '#0f766e', project_id: 'none' });
      await loadWorkspace();
    } finally {
      setSaving(false);
    }
  }

  async function updateProject(id: string, updates: Record<string, unknown>) {
    await api.workspace.updateProject(id, updates);
    await loadWorkspace();
  }

  async function updateBundle(id: string, updates: Record<string, unknown>) {
    await api.workspace.updateBundle(id, {
      ...updates,
      project_id: updates.project_id === 'none' ? null : updates.project_id,
    });
    await loadWorkspace();
  }

  async function deleteProject(id: string) {
    await api.workspace.deleteProject(id);
    await loadWorkspace();
  }

  async function deleteBundle(id: string) {
    await api.workspace.deleteBundle(id);
    await loadWorkspace();
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-5">
      <header className="border-b pb-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>↗</span>
          <span>Workspace</span>
        </div>
        <h1 className="mt-2 text-3xl font-semibold tracking-normal">Activity system</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Create the bundles and projects you want to plan with. Every activity belongs to a user-created bundle; projects stay optional.
        </p>
      </header>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-3">
          <DatabaseHeader icon={Layers} title="Bundles" count={activeBundles.length} />
          <div className="overflow-hidden rounded-md border bg-card">
            <div className="grid grid-cols-[minmax(160px,1fr)_minmax(140px,220px)_120px_40px] gap-2 border-b bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <span>Name</span>
              <span>Project</span>
              <span>Status</span>
              <span />
            </div>
            {activeBundles.length === 0 && (
              <div className="px-3 py-8 text-sm text-muted-foreground">
                Create your first bundle before adding activities.
              </div>
            )}
            {activeBundles.map(bundle => (
              <div
                key={bundle.id}
                className="grid grid-cols-[minmax(160px,1fr)_minmax(140px,220px)_120px_40px] items-center gap-2 border-b px-3 py-2 last:border-b-0"
              >
                <InlineName
                  value={bundle.name}
                  purpose={bundle.purpose || ''}
                  color={bundle.color}
                  onName={name => updateBundle(bundle.id, { name })}
                  onPurpose={purpose => updateBundle(bundle.id, { purpose })}
                  onColor={color => updateBundle(bundle.id, { color })}
                />
                <Select
                  value={bundle.projectId || 'none'}
                  onValueChange={project_id => updateBundle(bundle.id, { project_id })}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project</SelectItem>
                    {activeProjects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Badge variant="secondary" className="w-fit">
                  Active
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteBundle(bundle.id)}
                  aria-label={`Delete ${bundle.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <CreationPanel
          title="New bundle"
          action="Add bundle"
          saving={saving}
          onSubmit={createBundle}
        >
          <DraftFields
            name={bundleDraft.name}
            purpose={bundleDraft.purpose}
            color={bundleDraft.color}
            onChange={updates => setBundleDraft(prev => ({ ...prev, ...updates }))}
          />
          <div className="grid gap-2">
            <Label>Project</Label>
            <Select
              value={bundleDraft.project_id}
              onValueChange={project_id => setBundleDraft(prev => ({ ...prev, project_id }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project</SelectItem>
                {activeProjects.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CreationPanel>
      </section>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-3">
          <DatabaseHeader icon={FolderKanban} title="Projects" count={activeProjects.length} />
          <div className="overflow-hidden rounded-md border bg-card">
            <div className="grid grid-cols-[minmax(160px,1fr)_120px_80px_40px] gap-2 border-b bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <span>Name</span>
              <span>Bundles</span>
              <span>Status</span>
              <span />
            </div>
            {activeProjects.map(project => (
              <div
                key={project.id}
                className="grid grid-cols-[minmax(160px,1fr)_120px_80px_40px] items-center gap-2 border-b px-3 py-2 last:border-b-0"
              >
                <InlineName
                  value={project.name}
                  purpose={project.purpose || ''}
                  color={project.color}
                  onName={name => updateProject(project.id, { name })}
                  onPurpose={purpose => updateProject(project.id, { purpose })}
                  onColor={color => updateProject(project.id, { color })}
                />
                <span className="text-sm text-muted-foreground">{bundlesByProject.get(project.id) || 0}</span>
                <Badge variant="outline" className="w-fit">
                  Active
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteProject(project.id)}
                  aria-label={`Delete ${project.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <CreationPanel
          title="New project"
          action="Add project"
          saving={saving}
          onSubmit={createProject}
        >
          <DraftFields
            name={projectDraft.name}
            purpose={projectDraft.purpose}
            color={projectDraft.color}
            onChange={updates => setProjectDraft(prev => ({ ...prev, ...updates }))}
          />
        </CreationPanel>
      </section>
    </div>
  );
}

function DatabaseHeader({
  icon: Icon,
  title,
  count,
}: {
  icon: LucideIcon;
  title: string;
  count: number;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <h2 className="text-lg font-semibold">{title}</h2>
      <span className="text-xs text-muted-foreground">{count}</span>
    </div>
  );
}

function InlineName({
  value,
  purpose,
  color,
  onName,
  onPurpose,
  onColor,
}: {
  value: string;
  purpose: string;
  color: string;
  onName: (name: string) => void;
  onPurpose: (purpose: string | null) => void;
  onColor: (color: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [purposeDraft, setPurposeDraft] = useState(purpose);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    setPurposeDraft(purpose);
  }, [purpose]);

  return (
    <div className="flex min-w-0 items-start gap-2">
      <ColorMenu value={color} onChange={onColor} />
      <div className="min-w-0 flex-1">
        <Input
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onBlur={() => draft.trim() && draft !== value && onName(draft.trim())}
          className="h-8 border-transparent bg-transparent px-1 text-sm shadow-none hover:border-input focus-visible:border-ring"
        />
        <Input
          value={purposeDraft}
          onChange={event => setPurposeDraft(event.target.value)}
          onBlur={() => {
            const next = purposeDraft.trim();
            if (next !== purpose) onPurpose(next || null);
          }}
          placeholder="Purpose"
          className="h-7 border-transparent bg-transparent px-1 text-xs text-muted-foreground shadow-none hover:border-input focus-visible:border-ring"
        />
      </div>
    </div>
  );
}

function CreationPanel({
  title,
  action,
  saving,
  children,
  onSubmit,
}: {
  title: string;
  action: string;
  saving: boolean;
  children: ReactNode;
  onSubmit: () => void;
}) {
  return (
    <aside className="rounded-md border bg-muted/20 p-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      <div className="mt-3 grid gap-3">{children}</div>
      <Button onClick={onSubmit} disabled={saving} className="mt-3 w-full">
        <Plus className="mr-2 h-4 w-4" />
        {action}
      </Button>
    </aside>
  );
}

function DraftFields({
  name,
  purpose,
  color,
  onChange,
}: {
  name: string;
  purpose: string;
  color: string;
  onChange: (updates: { name?: string; purpose?: string; color?: string }) => void;
}) {
  return (
    <>
      <div className="grid gap-2">
        <Label>Name</Label>
        <Input value={name} onChange={event => onChange({ name: event.target.value })} />
      </div>
      <div className="grid gap-2">
        <Label>Purpose</Label>
        <Textarea
          value={purpose}
          onChange={event => onChange({ purpose: event.target.value })}
          className="min-h-20"
        />
      </div>
      <div className="grid gap-2">
        <Label>Color</Label>
        <div className="flex flex-wrap gap-1.5">
          {palette.map(option => (
            <button
              key={option}
              type="button"
              onClick={() => onChange({ color: option })}
              className={cn(
                'h-7 w-7 rounded-md border',
                color === option && 'ring-2 ring-ring ring-offset-2'
              )}
              style={{ backgroundColor: option }}
              aria-label={`Use ${option}`}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function ColorMenu({ value, onChange }: { value: string; onChange: (color: string) => void }) {
  return (
    <div className="flex items-center gap-1">
      {palette.slice(0, 1).map(() => (
        <button
          key={value}
          type="button"
          className="h-4 w-4 shrink-0 rounded-sm border"
          style={{ backgroundColor: value }}
          aria-label="Current color"
        />
      ))}
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        className="h-7 w-8 rounded border bg-background text-transparent"
        aria-label="Change color"
      >
        {palette.map(option => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}
